// src/pages/dashboard/DashboardRRA.jsx
import React, { useEffect, useRef, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '../../../services/AuthService';
import AuthContext from '../../../context/Auth/AuthContext';
import '../dashboard/ca_dash.css';
import "@fortawesome/fontawesome-free/css/all.css";

// Helpers -------------------------------------------------------------
const toNumber = (v) => {
  if (typeof v === 'number') return v;
  const s = String(v ?? '0').trim();

  // en-US puro (apenas dígitos e UM ponto, sem vírgula) -> respeita o ponto como decimal
  if (/^-?\d+(\.\d+)?$/.test(s) && !s.includes(',')) {
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  }

  // pt-BR / com milhares -> remove '.' de milhar e troca vírgula por ponto
  const n = Number(s.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const formatBRL = (n) => toNumber(n).toLocaleString('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const formatDateBR = (d) => {
  if (!d) return '—';
  const dt = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(`${d}T00:00:00`) : new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('pt-BR');
};

const getCreateDate = (obj) => obj?.create_date || obj?.creation_date || obj?.data_create || obj?.createDate;

// dd/mm/aaaa
const toBRDate = (d) => {
  if (!d) return undefined;
  const s = String(d);
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  let dt;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) dt = new Date(`${s}T00:00:00`);
  else dt = new Date(s);
  if (Number.isNaN(dt.getTime())) return undefined;
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

// "1234,56" a partir de decimal en-US ou pt-BR
const asBRFromDecimal = (v) => {
  const s = String(v ?? '0').trim();
  let n;
  if (/^-?\d+(\.\d+)?$/.test(s) && !s.includes(',')) {
    n = Number(s); // respeita o ponto como decimal
  } else {
    n = Number(s.replace(/\./g, '').replace(',', '.'));
  }
  if (!Number.isFinite(n)) n = 0;
  const [int, frac] = n.toFixed(2).split('.');
  return `${int},${frac}`;
};

// "1234,56" a partir de VALOR EM CENTAVOS (divide por 100)
const asBRFromCentavos = (v) => {
  let n = Number(String(v ?? '0').replace(',', '.'));
  if (!Number.isFinite(n)) n = 0;
  n = n / 100;
  return n
    .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    .replace(/\u00A0/g, ' ');
};

// "M" para Municipal, "E" para Estadual
const mapMunicipalEstadual = (v) => {
  const s = String(v ?? '').trim().toUpperCase();
  return s.startsWith('E') ? 'E' : 'M';
};

const buildPrefillFromRow = (row) => {
  const hasContagem =
    row?.contagem_meses != null && String(row.contagem_meses).trim() !== '';

  const me = mapMunicipalEstadual(row?.tipo_desagio); // "M" ou "E"
  const desagioSN = Number(row?.desagio) === 1 ? 'SIM' : 'NAO';
  const decimoSN  = Number(row?.acrescimo) === 1 ? 'SIM' : 'NAO';

  return {
    // campos lidos pelo form (topo)
    numero_processo: row?.numero_processo ?? '',
    nome: row?.nome ?? '',

    // valores com vírgula (evita .00 extra)
    valor_principal: asBRFromCentavos(
      toNumber(row?.principal_inicial) +
      toNumber(row?.medica_inicial) +
      toNumber(row?.previdenciario_inicial)
    ),
    valor_moratorio:      asBRFromCentavos(row?.moratorio_inicial),
    valor_assistencia:    asBRFromCentavos(row?.medica_inicial),
    valor_previdenciario: asBRFromCentavos(row?.previdenciario_inicial),

    // datas dd/mm/yyyy
    data_base:        toBRDate(row?.data_base),
    data_oficio:      toBRDate(row?.oficio),
    data_atualizacao: toBRDate(row?.data_att),

    // honorários (%)
    ...(row?.porcentangem_honorario != null
      ? { honorarios: String(row.porcentangem_honorario) }
      : {}),

    // *** duplicata no topo para quem usa tipo_desagio diretamente ***
    tipo_desagio: me, // "M" ou "E"

    // RRA precisa vir aninhado
    rra: {
      codigo_o_c: row?.oc ?? '',
      municipal_estadual: mapMunicipalEstadual(row?.tipo_desagio),
      desagio: desagioSN,        // "SIM"/"NAO"
      decimo_terceiro: decimoSN, // "SIM"/"NAO"

      ...(hasContagem
        ? {
            contagem_meses: String(row?.contagem_meses).replace(/\D/g, ''),
            data_inicial: '',
            data_final: '',
          }
        : {
            data_inicial: toBRDate(row?.data_inicial_rra),
            data_final:   toBRDate(row?.data_final_rra),
            contagem_meses: '',
          }
      ),
    },

    // Se um dia quiser pré-marcar prioridade, dá pra usar:
    // prioridade: {
    //   enabled: row?.data_adiantamento != null && String(row.data_adiantamento).trim() !== '',
    //   data: toBRDate(row?.data_adiantamento),
    // },
  };
};
// <<< FIM DAS ADIÇÕES PARA PREFILL >>>


// Modal ---------------------------------------------------------------
const ModalRRA = ({ data, onClose }) => {
  if (!data) return null;

  const totalAtualizado =
    toNumber(data.principal_final) +
    toNumber(data.moratorio_final) +
    toNumber(data.ipesp_iamsp_final);

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
      }}
    >
      <div
        className="modal-content modal-rra"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header centralizado (sem botão fechar) */}
        <div className="modal-rra__header modal-rra__header--center">
          <h2 className="modal-rra__title">Detalhes do Cálculo</h2>
        </div>

        {/* Identificação centralizada */}
        <div className="modal-rra__id-grid modal-rra__id-grid--center">
          <div className="id-block">
            <div className="label">Nome</div>
            <div className="value">{data.nome || '—'}</div>
          </div>

          <div className="id-block">
            <div className="label">Número do Processo</div>
            <div className="value">{data.numero_processo || '—'}</div>
          </div>

          <div className="id-block">
            <div className="label">Data da Solicitação</div>
            <div className="value">{formatDateBR(getCreateDate(data))}</div>
          </div>
        </div>

        {/* 3 KPIs centralizados e divididos */}
        <div className="kpi-grid kpi-grid--3">
          <div className="kpi-card kpi-center kpi-divided">
            <div className="kpi-label">Principal Final</div>
            <div className="kpi-value">{formatBRL(data.principal_final)}</div>
          </div>
          <div className="kpi-card kpi-center kpi-divided">
            <div className="kpi-label">Moratório Final</div>
            <div className="kpi-value">{formatBRL(data.moratorio_final)}</div>
          </div>
          <div className="kpi-card kpi-center">
            <div className="kpi-label">IPESP/IAMSP Final</div>
            <div className="kpi-value">{formatBRL(data.ipesp_iamsp_final)}</div>
          </div>
        </div>

        {/* Total centralizado */}
        <div className="modal-footer-total modal-footer-total--center">
          <span>Valor total atualizado</span>
          <strong className="total-pill">{formatBRL(totalAtualizado)}</strong>
        </div>
      </div>
    </div>
  );
};

// Página --------------------------------------------------------------
const DashboardRRA = () => {
  const navigate = useNavigate();
  const headerRef   = useRef(null);
  const itemRef     = useRef(null);
  const { user }    = useContext(AuthContext);

  const [dataList, setDataList] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch]     = useState('');

  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [currentPage, setCurrentPage]   = useState(1);

  // Busca dados -------------------------------------------------------
  const fetchData = async () => {
    if (!user?.user?.id) {
      setError('Erro: Usuário não está autenticado corretamente.');
      setLoading(false);
      return;
    }
    const payload = { user_ids: [user.user.id] };
    try {
      // Implemente AuthService.fetchCalculos(payload) para retornar um array
      const resp = await AuthService.calConsult(user.user.id);
      const list = Array.isArray(resp) ? resp : [];
      list.sort((a, b) => {
        const da = new Date(getCreateDate(a) || 0).getTime();
        const db = new Date(getCreateDate(b) || 0).getTime();
        return db - da; // desc
      });
      setDataList(list);
    } catch (e) {
      console.error('Erro ao carregar cálculos:', e);
      setError('Erro ao carregar os cálculos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [user?.user?.id]);

  // Resize-aware items per page --------------------------------------
  useEffect(() => {
    const calculateItemsPerPage = () => {
      if (!headerRef.current || !itemRef.current) return;
      const headerH = headerRef.current.offsetHeight;
      const itemH   = itemRef.current.offsetHeight || 96; // fallback
      const available = window.innerHeight - headerH;
      const count = Math.floor(available / itemH);
      setItemsPerPage(count > 0 ? count : 1);
    };
    calculateItemsPerPage();
    window.addEventListener('resize', calculateItemsPerPage);
    return () => window.removeEventListener('resize', calculateItemsPerPage);
  }, []);

  // Filtros e paginação ----------------------------------------------
  const filtered = dataList.filter((it) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase().trim();
    return (
      String(it.numero_processo || '').toLowerCase().includes(s) ||
      String(it.nome || '').toLowerCase().includes(s)
      // removido: || String(it.uuid || '').toLowerCase().includes(s)
    );
  });

  useEffect(() => { setCurrentPage(1); }, [search]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const pageData   = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Ações -------------------------------------------------------------
  const handleReload = () => { setLoading(true); fetchData(); };
  const handleOpen   = (row) => setExpanded(row);
  const handleClose  = () => setExpanded(null);

  const handleRefazer = (e, row) => {
    e.stopPropagation();
    const prefill = buildPrefillFromRow(row);
    navigate('/home/calculation/manual', { state: { prefill } });
  };

  const handleGerarPDF = async (e, row) => {
    e.stopPropagation();
    try {
      // Implemente AuthService.generateCalculoPdf(uuid) para retornar um Blob (application/pdf)
      const blob = await AuthService.generateCalculoPdf(row.uuid);
      if (!(blob instanceof Blob)) {
        console.error('Resposta inválida ao gerar PDF');
        return alert('Erro ao gerar o PDF.');
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `calculo-${row.numero_processo || row.uuid || 'documento'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
      alert('Erro ao gerar PDF.');
    }
  };

  // Render de cada item ----------------------------------------------
  const renderItem = (row, idx) => {
    const refProps = idx === 0 ? { ref: itemRef } : {};

    return (
      <div
        key={row.uuid || row.id || `${row.numero_processo}-${idx}`}
        className="dashboard-item"
        onClick={() => handleOpen(row)}
        {...refProps}
      >
        <div className="dashboard-item-row">
          <div className="dashboard-item-content" style={{ minWidth: 0, flex: '1 1 auto' }}>
            {/* Título: Número do processo grande */}
            <h3 style={{marginBottom: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',}}>
              {row.numero_processo || '—'}
            </h3>

            {/* Linha de detalhes: Nome + Data */}
            <div className="dashboard-item-details" style={{ minWidth: 0, overflow: 'hidden' }}>
              <span>Nome: {row.nome || '—'}</span>
              <span className="dot-separator">•</span>
              <span>Data da Solicitação: {formatDateBR(getCreateDate(row))}</span>
            </div>
          </div>

          {/* Ações no lugar do status */}
          <div
            className="dashboard-item-status"
            style={{
              display: 'flex',
              gap: 8,
              flexWrap: 'nowrap',
              marginLeft: 'auto',   // puxa o grupo para a direita
              flex: '0 0 auto',     // não deixa o grupo encolher
              whiteSpace: 'nowrap', // sem quebra de linha
            }}
          >
            <button
              className="btn btn-secondary btn-rra-grow"
              onClick={(e) => handleRefazer(e, row)}
              title="Refazer Solicitação"
            >
              Refazer Solicitação
            </button>

            <button
              className="btn btn-primary btn-rra-fixed"
              onClick={(e) => handleGerarPDF(e, row)}
              title="Gerar PDF"
            >
              Gerar PDF
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Estados base ------------------------------------------------------
  if (loading)      return <div style={{ color: '#fff', padding: 24 }}>Carregando...</div>;
  if (error)        return <div style={{ color: '#fff', padding: 24 }}>{error}</div>;

  // UI ----------------------------------------------------------------
  return (
    <>
      <div ref={headerRef} className="dashboard-header">
        <div className="header-row--top">
          <div className="header-titles">
            <h1>Cálculos</h1>
            <h2 className="dashboard-subtitle">Gerencie os cálculos solicitados</h2>
          </div>
          <button className="request-btn" onClick={() => navigate('/home/calculation')}>
            Solicitar Cálculo
          </button>
        </div>

        <div className="header-row--bottom">
          <div className="header-actions">
            <input
              type="text"
              placeholder="Pesquisar por Nº do processo ou nome"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
            <i
              className="fas fa-sync-alt reload-icon"
              onClick={handleReload}
              title="Recarregar"
            />
          </div>
        </div>
      </div>

      <div className="dashboard-div">
        <div className="dashboard-container">
          {pageData.length > 0 ? (
            pageData.map((row, i) => renderItem(row, i))
          ) : (
            <p style={{ color: '#fff', textAlign: 'center', margin: '32px 0' }}>
              Nenhum cálculo encontrado
            </p>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1}>
              « Anterior
            </button>
            <span>{currentPage} de {totalPages}</span>
            <button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>
              Próximo »
            </button>
          </div>
        )}
      </div>

      {expanded && <ModalRRA data={expanded} onClose={handleClose} />}
    </>
  );
};

export default DashboardRRA;
